import { createSelector, createSlice } from "@reduxjs/toolkit";
import { isValidNumber } from "../../components/Converter";
import berechneAussere from "../../services/berechneAussere.service";
import { reset } from "../resetParameters";
import { selectBemessungslastGruppeGesamt } from "./bemessungslastGruppeGesamtSlice";
import { selectBemessungslast } from "./bemessungslastSlice";
import { selectIsTransmissionLengthValid } from "./selectors/Transmission";

const initialState = {
  modelFaktor: 1,
  teilsicherheitsbeiwert: 1.4,
  pileCapExtension: 0.5,

  pfahlneigungZurVertikalen: 0,

  pfahllaengeGewaehltInBoden: 0,
  pfahllaengeGewaehltInBodenIsCustom: false,

  la: Number.NaN,
  lb: Number.NaN,
  schichtenInput: [
    {
      index: 0,
      // TODO: should be set by the table, no translation for default values
      schichtName: "1",
      von: 0,
      bis: Number.MAX_SAFE_INTEGER,
      qski: Number.NaN,
      a: 20,
    },
  ],
  schichtenCalculated: [
    {
      index: 0,
      rdi: 0,
      liZeta: 0,
      rsdi: 0,
      fdRest: 0,
      ti: 0,
    },
  ],
  lreq: 0, // Min. pile length ???
  sumRsdi: 0,
  bemessungspfahlwiderstand: 0, // rsd
  dGruppe: {
    schichtenCalculated: [
      {
        index: 0,
        rdi: 0,
        liZeta: 0,
        rsdi: 0,
        fdRest: 0,
        ti: 0,
      },
    ],
    lreq: 0,
    sumRsdi: 0,
    bemessungspfahlwiderstand: 0,
  },
  perimeterPileGroup: 0,
  transmission: {
    teilsicherheitsbeiwert: 1.5,
    verbundbedingungenEta1: 1,
    druckfestigkeitZement: 2.2,
    betonzugfestigkeit: "35",
  },
  zgruppe: {
    BSP: {
      YGD: 1.05,
      YQD: 1.5,
      YGS: 0.95,
    },
    staendigeDestabilisierendeEinwirkungen: 0,
    veraenderlicheDestabilisierendeEinwirkungen: 0,
    staendigeStabilisierendeEinwirkungen: 0,
    wichteBoden: 0,
    anpassungsfaktor: 0.8,

    // NaN for inital empty Field
    groesseresPfahlraster: Number.NaN,
    kleineresPfahlraster: Number.NaN,
    reibungswinkelBoden: Number.NaN,
    anzahlPfaehle: Number.NaN,
    lReq: Number.NaN,
    lReqIsValid: true,
    left: Number.NaN,
    right: Number.NaN,
  },
};

const aeussereSlice = createSlice({
  name: "aeussere",
  initialState,
  reducers: {
    setAeussereState(state, action) {
      state.dGruppe = action.payload.dGruppe;
      state.transmission = action.payload.transmission;
      state.zgruppe = action.payload.zgruppe;
      state.schichtenInput = action.payload.schichtenInput;
      state.lreq = action.payload.lreq;

      state.modelFaktor = action.payload.modelFaktor;
      state.teilsicherheitsbeiwert = action.payload.teilsicherheitsbeiwert;
      state.pileCapExtension = action.payload.pileCapExtension;
      state.pfahlneigungZurVertikalen = action.payload.pfahlneigungZurVertikalen;
      state.pfahllaengeGewaehltInBoden = action.payload.pfahllaengeGewaehltInBoden;
      state.pfahllaengeGewaehltInBodenIsCustom = action.payload.pfahllaengeGewaehltInBodenIsCustom;
      state.la = action.payload.la;
      state.lb = action.payload.lb;
      state.sumRsdi = action.payload.sumRsdi;
      state.bemessungspfahlwiderstand = action.payload.bemessungspfahlwiderstand;
      state.perimeterPileGroup = action.payload.perimeterPileGroup;
    },
    setPileCapExtension(state, action) {
      state.pileCapExtension = action.payload;
    },
    setBSP(state, action) {
      state.zgruppe.BSP = {
        ...state.zgruppe.BSP,
        ...action.payload,
      };
    },
    setAnpassungsfaktor(state, action) {
      state.zgruppe.anpassungsfaktor = action.payload;
    },
    setGroesseresPfahlraster(state, action) {
      state.zgruppe.groesseresPfahlraster = action.payload;
    },
    setKleineresPfahlraster(state, action) {
      state.zgruppe.kleineresPfahlraster = action.payload;
    },
    setReibungswinkelBoden(state, action) {
      state.zgruppe.reibungswinkelBoden = action.payload;
    },
    setAnzahlPfaehle(state, action) {
      state.zgruppe.anzahlPfaehle = action.payload;
    },
    setWichteBoden(state, action) {
      state.zgruppe.wichteBoden = action.payload;
    },
    setStaendigeDestabilisierendeEinwirkungen(state, action) {
      state.zgruppe.staendigeDestabilisierendeEinwirkungen = action.payload;
    },
    setVeraenderlicheDestabilisierendeEinwirkungen(state, action) {
      state.zgruppe.veraenderlicheDestabilisierendeEinwirkungen = action.payload;
    },
    setStaendigeStabilisierendeEinwirkungen(state, action) {
      state.zgruppe.staendigeStabilisierendeEinwirkungen = action.payload;
    },
    addSchicht(state, action) {
      state.schichtenInput.push(action.payload);

      let neueSchicht = {};
      Object.assign(neueSchicht, initialState.schichtenCalculated[0]);
      neueSchicht.index = action.payload.index;

      state.schichtenCalculated.push(neueSchicht);
    },
    setSchichtenCalculated(state, action) {
      state.schichtenCalculated = action.payload;
    },
    deleteSchicht(state, action) {
      const { position } = action.payload;
      state.schichtenInput.splice(position, 1);
      state.schichtenCalculated.splice(position, 1);
      state.dGruppe?.schichtenCalculated?.splice(position, 1);
    },
    updateSchichtenCalculatedValue(state, action) {
      const { index } = action.payload;
      state.schichtenCalculated[index] = {
        ...state.schichtenCalculated[index],
        ...action.payload,
      };
    },
    updateSchichtenValue(state, action) {
      const { index } = action.payload;
      state.schichtenInput[index] = {
        ...state.schichtenInput[index],
        ...action.payload,
      };
    },
    setLreq(state, action) {
      state.lreq = action.payload;
    },
    setSumRsdi(state, action) {
      state.sumRsdi = action.payload;
    },
    setBemessungspfahlwiderstand(state, action) {
      state.bemessungspfahlwiderstand = action.payload;
    },
    setPfahllaengeGewaehltInBoden(state, action) {
      state.pfahllaengeGewaehltInBoden = action.payload;
    },

    setPfahllaengeGewaehltInBodenIsCustom(state, action) {
      state.pfahllaengeGewaehltInBodenIsCustom = action.payload;
    },

    setPfahlneigungZurVertikalen(state, action) {
      state.pfahlneigungZurVertikalen = action.payload;
    },
    setPerimeterPileGroup(state, action) {
      state.perimeterPileGroup = action.payload;
    },
    updateDGruppeValue(state, action) {
      state.dGruppe = {
        ...state.dGruppe,
        ...action.payload,
      };
    },
    setLa(state, action) {
      state.la = action.payload;
    },
    setLb(state, action) {
      state.lb = action.payload;
    },
    setTeilsicherheitsbeiwert(state, action) {
      state.teilsicherheitsbeiwert = action.payload;
    },
    setModelFaktor(state, action) {
      state.modelFaktor = action.payload;
    },
    setDruckfestigkeitZement(state, action) {
      state.transmission.druckfestigkeitZement = action.payload;
    },
    setBetonzugfestigkeit(state, action) {
      state.transmission.betonzugfestigkeit = action.payload;
    },
    setZGruppeLReq(state, action) {
      state.zgruppe.lReq = action.payload;
    },
    setZGruppeLReqIsValid(state, action) {
      state.zgruppe.lReqIsValid = action.payload;
    },
    setZGruppeLeft(state, action) {
      state.zgruppe.left = action.payload;
    },
    setZGruppeRight(state, action) {
      state.zgruppe.right = action.payload;
    },
    setVerbundbedingungenEta1(state, action) {
      state.transmission.verbundbedingungenEta1 = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(reset, (state, action) => {
      return initialState;
    });
  },
});

export const {
  setAeussereState,
  setPileCapExtension,
  setBSP,
  setAnpassungsfaktor,
  setGroesseresPfahlraster,
  setKleineresPfahlraster,
  setReibungswinkelBoden,
  setAnzahlPfaehle,
  setWichteBoden,
  setStaendigeDestabilisierendeEinwirkungen,
  setVeraenderlicheDestabilisierendeEinwirkungen,
  setStaendigeStabilisierendeEinwirkungen,
  addSchicht,
  setSchichtenCalculated,
  deleteSchicht,
  updateSchichtenCalculatedValue,
  updateSchichtenValue,
  setLreq,
  setSumRsdi,
  setBemessungspfahlwiderstand,
  setPfahllaengeGewaehltInBoden,
  setPfahllaengeGewaehltInBodenIsCustom,
  setPfahlneigungZurVertikalen,
  setPerimeterPileGroup,
  updateDGruppeValue,
  setLa,
  setLb,
  setTeilsicherheitsbeiwert,
  setModelFaktor,
  setDruckfestigkeitZement,
  setBetonzugfestigkeit,
  setZGruppeLReq,
  setZGruppeLReqIsValid,
  setZGruppeLeft,
  setZGruppeRight,
  setVerbundbedingungenEta1,
} = aeussereSlice.actions;

export default aeussereSlice.reducer;

// #region selectors
export const selectAeussere = (state) => state.aeussere;
export const selectDruckpfahlgruppe = (state) => state.aeussere.dGruppe;

export const selectMinimalePfahllaenge = (state) => state.aeussere.lreq;

export const selectLaengeGruppeErfordelich = (state) => state.aeussere.dGruppe.lreq;
export const selectRsdi = (state) => state.aeussere.rsdi;
export const selectPfahlneigungZurVertikalen = (state) => state.aeussere.pfahlneigungZurVertikalen;
export const selectPfahllaengeGewaehltInBoden = (state) =>
  state.aeussere.pfahllaengeGewaehltInBoden;

export const selectPfahllaengeGewaehltInBodenIsCustom = (state) =>
  state.aeussere.pfahllaengeGewaehltInBodenIsCustom;
export const selectSchichtenCalculated = (state) => state.aeussere.schichtenCalculated;
export const selectSchichteninput = (state) => state.aeussere.schichtenInput;
export const selectBemessungspfahlwiderstand = (state) => state.aeussere.bemessungspfahlwiderstand;
export const selectModelFaktor = (state) => state.aeussere.modelFaktor;
export const selectPileCapExtension = (state) => state.aeussere.pileCapExtension;
export const selectTeilsicherheitsbeiwert = (state) => state.aeussere.teilsicherheitsbeiwert;

export const selectTeilsicherheitsbeiwertTransmission = (state) =>
  state.aeussere.transmission.teilsicherheitsbeiwert;
export const selectVerbundbedingungenEta1 = (state) =>
  state.aeussere.transmission.verbundbedingungenEta1;

export const selectDruckfestigkeitZement = (state) =>
  state.aeussere.transmission.druckfestigkeitZement;
export const selectBetonzugfestigkeit = (state) => state.aeussere.transmission.betonzugfestigkeit;

export const selectPerimeterPileGroup = (state) => state.aeussere.perimeterPileGroup;
export const selectLa = (state) => state.aeussere.la;
export const selectLb = (state) => state.aeussere.lb;

export const selectBSP = (state) => state.aeussere.zgruppe.BSP;

export const selectAnpassungsfaktor = (state) => state.aeussere.zgruppe.anpassungsfaktor;

export const selectStaendigeDestabilisierendeEinwirkungen = (state) =>
  state.aeussere.zgruppe.staendigeDestabilisierendeEinwirkungen;
export const selectVeraenderlicheDestabilisierendeEinwirkungen = (state) =>
  state.aeussere.zgruppe.veraenderlicheDestabilisierendeEinwirkungen;
export const selectStaendigeStabilisierendeEinwirkungen = (state) =>
  state.aeussere.zgruppe.staendigeStabilisierendeEinwirkungen;
export const selectWichteBoden = (state) => state.aeussere.zgruppe.wichteBoden;

export const selectGroesseresPfahlraster = (state) => state.aeussere.zgruppe.groesseresPfahlraster;
export const selectKleineresPfahlraster = (state) => state.aeussere.zgruppe.kleineresPfahlraster;
export const selectReibungswinkelBoden = (state) => state.aeussere.zgruppe.reibungswinkelBoden;
export const selectAnzahlPfaehle = (state) => state.aeussere.zgruppe.anzahlPfaehle;
export const selectZgruppeLreq = (state) => state.aeussere.zgruppe.lReq;
export const selectZgruppeLreqIsValid = (state) => state.aeussere.zgruppe.lReqIsValid;
export const selectZgruppeLeft = (state) => state.aeussere.zgruppe.left;
export const selectZgruppeRight = (state) => state.aeussere.zgruppe.right;

// #endregion

export const selectLongestPfahlValue = createSelector(
  [selectPfahllaengeGewaehltInBoden, selectMinimalePfahllaenge],
  (pfahllaengeGewaehltInBoden, minimalePfahllaenge) => {
    return Number(pfahllaengeGewaehltInBoden) < Number(minimalePfahllaenge)
      ? minimalePfahllaenge
      : pfahllaengeGewaehltInBoden;
  }
);

export const selectLongestPfahlValuePlusExtension = createSelector(
  [selectLongestPfahlValue, selectPileCapExtension],
  (longestPfahlValue, pileCapExtension) => {
    return Number(pileCapExtension) + Number(longestPfahlValue);
  }
);

// TODO: split into smaller parts
export const selectBerechnet = createSelector(
  [
    selectSchichtenCalculated,
    selectBemessungslast,
    selectBemessungspfahlwiderstand,
    selectPfahllaengeGewaehltInBoden,
    selectMinimalePfahllaenge,
  ],
  (
    schichtenCalculated,
    bemessungslast,
    bemessungspfahlwiderstand,
    pfahllaengeGewaehltInBoden,
    minimalePfahllaenge
  ) => {
    return berechneAussere(
      schichtenCalculated,
      bemessungslast,
      bemessungspfahlwiderstand,
      pfahllaengeGewaehltInBoden,
      minimalePfahllaenge
    );
  }
);

export const selectIsUtilizationFactorValid = createSelector([selectBerechnet], (berechnet) => {
  let util = berechnet?.utilizationFactor;
  // TODO: decide what to do with values that are rounded. Can show as valid in UI but are actually not.
  return Number(util.toFixed(3)) < 1.001 && util > 0;
});

// Druckpfahlgruppe
export const selectIsLreqFactorValid = createSelector(
  [selectLaengeGruppeErfordelich, selectPfahllaengeGewaehltInBoden],
  (laengeGruppeErfordelich, laengeEinzelpfahl) => {
    if (!isValidNumber(laengeGruppeErfordelich) || !isValidNumber(laengeEinzelpfahl)) {
      return false;
    }
    let test =
      Number(Number(laengeGruppeErfordelich)?.toFixed(2)) <=
      Number(Number(laengeEinzelpfahl)?.toFixed(2));

    return test;
  }
);

export const selectShowError_Druckpfahlgruppe = createSelector(
  [selectIsLreqFactorValid, selectBemessungslastGruppeGesamt, selectLa, selectLb],
  (isLreqValid, bemessungslastGruppeGesamt, la, lb) => {
    let showErrorResult = bemessungslastGruppeGesamt && la && lb ? !isLreqValid : false;
    return showErrorResult;
  }
);

//ZugpfahlGruppe
export const selectShowError_Zugpfahlgruppe = createSelector(
  [selectZgruppeLreqIsValid],
  (isLreqValid) => {
    return !isLreqValid;
  }
);

export const selectShowError = createSelector(
  [
    selectIsUtilizationFactorValid,
    selectShowError_Druckpfahlgruppe,
    selectIsTransmissionLengthValid,
  ],
  (isUtilizationValid, showLreqError, isTransmissionLengthValid) => {
    return !isUtilizationValid || showLreqError || !isTransmissionLengthValid;
  }
);
